import { useMemo } from 'react';
import { metrics } from '@data/metrics';
import { plants } from '@data/plants';
import { tasks } from '@data/tasks';

export default function Analytics() {
	const latest = metrics.slice(-24); // last 24 hours
	const weekData = metrics.slice(-168); // last 7 days (24*7)
	
	// Calculate averages
	const avgTemp = parseFloat((latest.reduce((a, b) => a + b.temperature, 0) / latest.length).toFixed(1));
	const avgHumidity = parseFloat((latest.reduce((a, b) => a + b.humidity, 0) / latest.length).toFixed(1));
	const avgMoisture = Math.round(latest.reduce((a, b) => a + b.soilMoisture, 0) / latest.length);
	
	// Calculate trends (compare last 12 hours vs previous 12 hours)
	const recent12h = latest.slice(-12);
	const previous12h = latest.slice(-24, -12);
	const tempTrend = recent12h.reduce((a, b) => a + b.temperature, 0) / 12 - previous12h.reduce((a, b) => a + b.temperature, 0) / 12;
	const humidityTrend = recent12h.reduce((a, b) => a + b.humidity, 0) / 12 - previous12h.reduce((a, b) => a + b.humidity, 0) / 12;
	const moistureTrend = recent12h.reduce((a, b) => a + b.soilMoisture, 0) / 12 - previous12h.reduce((a, b) => a + b.soilMoisture, 0) / 12;
	
	// Calculate Garden Health Score
	const gardenHealthScore = useMemo(() => {
		// Optimal: temp 20-25°C, humidity 50-60%, moisture 40-60%
		let score = 100;
		if (avgTemp < 18 || avgTemp > 28) score -= 20;
		else if (avgTemp < 20 || avgTemp > 25) score -= 10;
		if (avgHumidity < 40 || avgHumidity > 70) score -= 15;
		else if (avgHumidity < 50 || avgHumidity > 60) score -= 8;
		if (avgMoisture < 30 || avgMoisture > 70) score -= 25;
		else if (avgMoisture < 40 || avgMoisture > 60) score -= 12;
		return Math.max(0, Math.min(100, Math.round(score)));
	}, [avgTemp, avgHumidity, avgMoisture]);
	
	// Get health status
	const healthStatus = gardenHealthScore >= 80 ? 'excellent' : gardenHealthScore >= 60 ? 'good' : gardenHealthScore >= 40 ? 'fair' : 'poor';
	const healthColor = healthStatus === 'excellent' ? 'success' : healthStatus === 'good' ? 'info' : healthStatus === 'fair' ? 'warning' : 'danger';
	
	// Plant health summary
	const plantHealthSummary = useMemo(() => {
		const good = plants.filter(p => p.health === 'good').length;
		const fair = plants.filter(p => p.health === 'fair').length;
		const poor = plants.filter(p => p.health === 'poor').length;
		return { good, fair, poor, total: plants.length };
	}, []);
	
	// Plants needing attention (low moisture or poor health)
	const plantsNeedingAttention = useMemo(() => {
		return plants.filter(p => p.soilMoisture < 35 || p.health === 'poor' || p.health === 'fair').slice(0, 3);
	}, []);
	
	// Upcoming tasks
	const upcomingTasks = useMemo(() => {
		return tasks.filter(t => t.status !== 'done').slice(0, 3);
	}, []);
	
	// Generate recommendations
	const recommendations = useMemo(() => {
		const recs: string[] = [];
		if (avgTemp < 18) recs.push('Temperature is low - consider moving plants to a warmer area');
		if (avgTemp > 28) recs.push('Temperature is high - ensure adequate ventilation');
		if (avgHumidity < 40) recs.push('Low humidity detected - misting plants may help');
		if (avgMoisture < 35) recs.push('Soil moisture is low - watering recommended');
		if (avgMoisture > 70) recs.push('Soil moisture is high - check for overwatering');
		if (plantsNeedingAttention.length > 0) recs.push(`${plantsNeedingAttention.length} plant(s) need attention`);
		if (recs.length === 0) recs.push('All conditions are optimal - keep up the great work!');
		return recs;
	}, [avgTemp, avgHumidity, avgMoisture, plantsNeedingAttention.length]);
	
	// Simple sparkline data (last 24 hours, 6 data points)
	const sparklineData = useMemo(() => {
		const points = 6;
		const step = Math.floor(latest.length / points);
		return {
			temp: Array.from({ length: points }, (_, i) => latest[i * step]?.temperature || 0),
			humidity: Array.from({ length: points }, (_, i) => latest[i * step]?.humidity || 0),
			moisture: Array.from({ length: points }, (_, i) => latest[i * step]?.soilMoisture || 0)
		};
	}, [latest]);
	
	// Helper to get status color for metrics
	const getStatusColor = (value: number, optimalMin: number, optimalMax: number, warningMin?: number, warningMax?: number) => {
		if (value >= optimalMin && value <= optimalMax) return 'success';
		if (warningMin && warningMax && value >= warningMin && value <= warningMax) return 'warning';
		return 'danger';
	};
	
	const tempColor = getStatusColor(avgTemp, 20, 25, 18, 28);
	const humidityColor = getStatusColor(avgHumidity, 50, 60, 40, 70);
	const moistureColor = getStatusColor(avgMoisture, 40, 60, 30, 70);
	
	// Color mapping for sparklines
	const getColorValue = (color: string) => {
		const colors: Record<string, string> = {
			success: '#198754',
			warning: '#ffc107',
			danger: '#dc3545',
			info: '#0dcaf0'
		};
		return colors[color] || '#6c757d';
	};
	
	// Simple sparkline component
	const Sparkline = ({ data, color, height = 30 }: { data: number[], color: string, height?: number }) => {
		const max = Math.max(...data, 1);
		const min = Math.min(...data, 0);
		const range = max - min || 1;
		const width = 80;
		const points = data.map((val, i) => {
			const x = (i / (data.length - 1)) * width;
			const y = height - ((val - min) / range) * height;
			return `${x},${y}`;
		}).join(' ');
		
		return (
			<svg width={width} height={height} style={{ display: 'block' }}>
				<polyline
					points={points}
					fill="none"
					stroke={getColorValue(color)}
					strokeWidth="2"
				/>
			</svg>
		);
	};

	return (
		<>
			<div className="d-flex justify-content-between align-items-center mb-4">
				<h1 className="h3 mb-0">Monitoring</h1>
				<span className="badge bg-secondary">Last 24 hours</span>
			</div>
			
			{/* Garden Health Score - Large Card */}
			<div className="row g-3 mb-4">
				<div className="col-12">
					<div className={`card border-${healthColor} border-2`}>
						<div className="card-body">
							<div className="row align-items-center">
								<div className="col-md-3 text-center">
									<div className={`display-1 fw-bold text-${healthColor}`}>
										{gardenHealthScore}
									</div>
									<div className="text-muted small">Health Score</div>
									<span className={`badge bg-${healthColor} mt-2`}>
										{healthStatus.charAt(0).toUpperCase() + healthStatus.slice(1)}
									</span>
								</div>
								<div className="col-md-9">
									<div className="d-flex align-items-center mb-2">
										<i className="bi bi-flower3 fs-4 text-success me-2"></i>
										<h5 className="mb-0">Garden Health Overview</h5>
									</div>
									<div className="row g-3 mt-2">
										<div className="col-4">
											<div className="d-flex align-items-center">
												<i className={`bi bi-thermometer-half text-${tempColor} me-2`}></i>
												<div>
													<div className="small text-muted">Temperature</div>
													<div className="fw-bold">{avgTemp}°C</div>
												</div>
											</div>
										</div>
										<div className="col-4">
											<div className="d-flex align-items-center">
												<i className={`bi bi-droplet text-${humidityColor} me-2`}></i>
												<div>
													<div className="small text-muted">Humidity</div>
													<div className="fw-bold">{avgHumidity}%</div>
												</div>
											</div>
										</div>
										<div className="col-4">
											<div className="d-flex align-items-center">
												<i className={`bi bi-moisture text-${moistureColor} me-2`}></i>
												<div>
													<div className="small text-muted">Soil Moisture</div>
													<div className="fw-bold">{avgMoisture}%</div>
												</div>
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
			
			{/* Metric Cards with Trends */}
			<div className="row g-3 mb-4">
				<div className="col-12 col-md-4">
					<div className={`card h-100 border-start border-${tempColor} border-4`}>
						<div className="card-body">
							<div className="d-flex justify-content-between align-items-start mb-2">
								<div>
									<div className="text-muted small mb-1">Temperature</div>
									<div className="d-flex align-items-baseline">
										<div className="display-6 fw-bold me-2">{avgTemp}°C</div>
										{Math.abs(tempTrend) > 0.5 && (
											<i className={`bi bi-arrow-${tempTrend > 0 ? 'up' : 'down'} text-${tempTrend > 0 ? 'danger' : 'info'}`}></i>
										)}
									</div>
								</div>
								<i className={`bi bi-thermometer-half fs-1 text-${tempColor} opacity-50`}></i>
							</div>
							<div className="mt-2">
								<Sparkline data={sparklineData.temp} color={tempColor} />
							</div>
							<div className="mt-2 small">
								<span className={`badge bg-${tempColor}-subtle text-${tempColor}-emphasis`}>
									{tempColor === 'success' ? 'Optimal' : tempColor === 'warning' ? 'Acceptable' : 'Needs Attention'}
								</span>
							</div>
						</div>
					</div>
				</div>
				
				<div className="col-12 col-md-4">
					<div className={`card h-100 border-start border-${humidityColor} border-4`}>
						<div className="card-body">
							<div className="d-flex justify-content-between align-items-start mb-2">
								<div>
									<div className="text-muted small mb-1">Humidity</div>
									<div className="d-flex align-items-baseline">
										<div className="display-6 fw-bold me-2">{avgHumidity}%</div>
										{Math.abs(humidityTrend) > 2 && (
											<i className={`bi bi-arrow-${humidityTrend > 0 ? 'up' : 'down'} text-${humidityTrend > 0 ? 'info' : 'warning'}`}></i>
										)}
									</div>
								</div>
								<i className={`bi bi-droplet fs-1 text-${humidityColor} opacity-50`}></i>
							</div>
							<div className="mt-2">
								<Sparkline data={sparklineData.humidity} color={humidityColor} />
							</div>
							<div className="mt-2 small">
								<span className={`badge bg-${humidityColor}-subtle text-${humidityColor}-emphasis`}>
									{humidityColor === 'success' ? 'Optimal' : humidityColor === 'warning' ? 'Acceptable' : 'Needs Attention'}
								</span>
							</div>
						</div>
					</div>
				</div>
				
				<div className="col-12 col-md-4">
					<div className={`card h-100 border-start border-${moistureColor} border-4`}>
						<div className="card-body">
							<div className="d-flex justify-content-between align-items-start mb-2">
								<div>
									<div className="text-muted small mb-1">Soil Moisture</div>
									<div className="d-flex align-items-baseline">
										<div className="display-6 fw-bold me-2">{avgMoisture}%</div>
										{Math.abs(moistureTrend) > 3 && (
											<i className={`bi bi-arrow-${moistureTrend > 0 ? 'up' : 'down'} text-${moistureTrend > 0 ? 'success' : 'warning'}`}></i>
										)}
									</div>
								</div>
								<i className={`bi bi-moisture fs-1 text-${moistureColor} opacity-50`}></i>
							</div>
							<div className="mt-2">
								<Sparkline data={sparklineData.moisture} color={moistureColor} />
							</div>
							<div className="mt-2 small">
								<span className={`badge bg-${moistureColor}-subtle text-${moistureColor}-emphasis`}>
									{moistureColor === 'success' ? 'Optimal' : moistureColor === 'warning' ? 'Acceptable' : 'Needs Attention'}
								</span>
							</div>
						</div>
					</div>
				</div>
			</div>
			
			{/* Plant Health & Recommendations Row */}
			<div className="row g-3 mb-4">
				{/* Plant Health Summary */}
				<div className="col-12 col-md-6">
					<div className="card h-100">
						<div className="card-header d-flex align-items-center">
							<i className="bi bi-flower3 me-2 text-success"></i>
							<span className="fw-bold">Plant Health</span>
						</div>
						<div className="card-body">
							<div className="row g-3 mb-3">
								<div className="col-4 text-center">
									<div className="display-6 fw-bold text-success">{plantHealthSummary.good}</div>
									<div className="small text-muted">Good</div>
								</div>
								<div className="col-4 text-center">
									<div className="display-6 fw-bold text-warning">{plantHealthSummary.fair}</div>
									<div className="small text-muted">Fair</div>
								</div>
								<div className="col-4 text-center">
									<div className="display-6 fw-bold text-danger">{plantHealthSummary.poor}</div>
									<div className="small text-muted">Poor</div>
								</div>
							</div>
							{plantsNeedingAttention.length > 0 && (
								<div>
									<div className="small text-muted mb-2">Needs Attention:</div>
									{plantsNeedingAttention.map(plant => (
										<div key={plant.id} className="d-flex justify-content-between align-items-center mb-2 p-2 bg-light rounded">
											<div>
												<div className="fw-bold">{plant.name}</div>
												<div className="small text-muted">{plant.location}</div>
											</div>
											<div className="text-end">
												<span className={`badge bg-${plant.health === 'poor' ? 'danger' : 'warning'}-subtle text-${plant.health === 'poor' ? 'danger' : 'warning'}-emphasis`}>
													{plant.health === 'poor' ? 'Poor' : 'Fair'}
												</span>
												<div className="small text-muted mt-1">Moisture: {plant.soilMoisture}%</div>
											</div>
										</div>
									))}
								</div>
							)}
						</div>
					</div>
				</div>
				
				{/* Recommendations */}
				<div className="col-12 col-md-6">
					<div className="card h-100">
						<div className="card-header d-flex align-items-center">
							<i className="bi bi-lightbulb me-2 text-warning"></i>
							<span className="fw-bold">Recommendations</span>
						</div>
						<div className="card-body">
							{recommendations.map((rec, idx) => (
								<div key={idx} className="d-flex align-items-start mb-3">
									<i className={`bi bi-${rec.includes('optimal') ? 'check-circle' : 'exclamation-triangle'} text-${rec.includes('optimal') ? 'success' : 'warning'} me-2 mt-1`}></i>
									<div className="small">{rec}</div>
								</div>
							))}
						</div>
					</div>
				</div>
			</div>
			
			{/* Recent Readings Table */}
			<div className="card">
				<div className="card-header d-flex justify-content-between align-items-center">
					<span className="fw-bold">Recent Readings</span>
					<span className="badge bg-secondary">Last 24 hours</span>
				</div>
				<div className="card-body">
					<div className="table-responsive">
						<table className="table table-sm table-hover">
							<thead>
								<tr>
									<th>Time</th>
									<th>Temperature</th>
									<th>Humidity</th>
									<th>Soil Moisture</th>
								</tr>
							</thead>
							<tbody>
								{latest.slice(-12).reverse().map(m => (
									<tr key={m.timestamp}>
										<td>{new Date(m.timestamp).toLocaleString('en-GB', { 
											month: 'short', 
											day: 'numeric', 
											hour: '2-digit', 
											minute: '2-digit' 
										})}</td>
										<td>
											<span className={`badge bg-${getStatusColor(m.temperature, 20, 25, 18, 28)}-subtle text-${getStatusColor(m.temperature, 20, 25, 18, 28)}-emphasis`}>
												{m.temperature.toFixed(1)}°C
											</span>
										</td>
										<td>
											<span className={`badge bg-${getStatusColor(m.humidity, 50, 60, 40, 70)}-subtle text-${getStatusColor(m.humidity, 50, 60, 40, 70)}-emphasis`}>
												{m.humidity.toFixed(1)}%
											</span>
										</td>
										<td>
											<span className={`badge bg-${getStatusColor(m.soilMoisture, 40, 60, 30, 70)}-subtle text-${getStatusColor(m.soilMoisture, 40, 60, 30, 70)}-emphasis`}>
												{m.soilMoisture}%
											</span>
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				</div>
			</div>
		</>
	);
}
